import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Clock, Users, Send, Gift } from "lucide-react";
import { SESSION_TYPES, SESSION_TYPE_LABEL, SPECIALIZATION_LABEL, SPECIALIZATION_COLOR } from "@/lib/constants";
import { toast } from "sonner";
import ReferDialog from "@/components/ReferDialog";
import RedeemDialog from "@/components/RedeemDialog";

function CoachRepsWallet() {
  const { data } = useQuery({
    queryKey: ["my-reps"],
    queryFn: async () => (await apiClient.get("/me/reps")).data,
  });
  const balance = data?.balance ?? 0;
  return (
    <div className="bg-gradient-to-r from-volt/10 to-zinc-950 border border-volt/30 rounded-md p-5 flex items-center gap-4" data-testid="coach-reps-wallet">
      <div className="w-12 h-12 grid place-items-center bg-volt text-black rounded-sm">
        <Gift size={22} />
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.22em] text-volt font-semibold">Your {data?.currency_name || "REPS"} Wallet</div>
        <div className="font-display text-3xl font-black text-volt mt-1">
          {balance} <span className="text-sm text-zinc-500">{data?.currency_name || "REPS"}</span>
          <span className="text-xs text-zinc-500 ml-3">≈ ₹{data?.approx_value_inr ?? 0}</span>
        </div>
        <div className="text-xs text-zinc-400 mt-1">Earn by referring prospects who convert. Redeem for cash, merch, recovery, PT.</div>
      </div>
      <RedeemDialog trigger={
        <Button data-testid="coach-redeem-button" className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
          Redeem
        </Button>
      } />
    </div>
  );
}


export default function CoachPage() {
  const { user, activeBranchId } = useAuth();
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [wodLevel, setWodLevel] = useState("Intermediate");
  const [wodFocus, setWodFocus] = useState("");

  const params = {};
  if (activeBranchId) params.branch_id = activeBranchId;
  if (user?.role === "COACH") params.coach_id = user.id;

  const { data: sessions = [] } = useQuery({
    queryKey: ["coach-sessions", activeBranchId, user?.id],
    queryFn: async () => (await apiClient.get("/sessions", { params })).data,
  });
  const { data: tasks = [] } = useQuery({
    queryKey: ["coach-tasks", user?.id],
    queryFn: async () => (await apiClient.get("/coach/tasks", { params: user?.role === "COACH" ? { coach_id: user.id } : {} })).data,
  });

  React.useEffect(() => {
    if (!selected && sessions.length > 0) setSelected(sessions[0]);
  }, [sessions, selected]);

  const updateSession = useMutation({
    mutationFn: async ({ id, body }) => (await apiClient.patch(`/sessions/${id}`, body)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["coach-sessions"] });
      setSelected(d);
      toast.success("Saved");
    },
  });

  const genWOD = useMutation({
    mutationFn: async () => (await apiClient.post(`/sessions/${selected.id}/generate-wod`, {
      session_type: selected.session_type, duration_min: selected.duration_min, fitness_level: wodLevel, focus: wodFocus || null,
    })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["coach-sessions"] });
      setSelected({ ...selected, wod: d.wod });
      toast.success("AI WOD generated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "AI failed"),
  });

  const completeTask = useMutation({
    mutationFn: async ({ id, body }) => (await apiClient.patch(`/coach/tasks/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-tasks"] }),
  });

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="coach-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Coach Hub</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Today's Sessions</h1>
          <div className="text-zinc-500 text-sm mt-1">Program, coach, and nudge your roster.</div>
        </div>
        <ReferDialog trigger={
          <Button data-testid="coach-refer-button" className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
            <Gift size={14} className="mr-1" /> Refer a Prospect
          </Button>
        } />
      </div>

      <CoachRepsWallet />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Session list */}
        <Panel className="lg:col-span-4">
          <PanelHeader subtitle="Schedule" title="Sessions" />
          <div className="space-y-2 max-h-[70vh] overflow-y-auto scrollbar-thin">
            {sessions.map((s) => (
              <button key={s.id} onClick={() => setSelected(s)}
                data-testid={`session-${s.id}`}
                className={`w-full text-left p-3 rounded-sm border transition-colors ${
                  selected?.id === s.id ? "bg-volt/10 border-volt" : "bg-zinc-950 border-zinc-900 hover:border-zinc-700"
                }`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{SESSION_TYPE_LABEL[s.session_type] || s.session_type}</div>
                    <div className="font-display text-lg font-bold uppercase">{s.title}</div>
                  </div>
                  <div className="font-mono-cmd text-xs text-volt">
                    {new Date(s.start_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                  <span><Clock size={10} className="inline mr-1" />{s.duration_min}m</span>
                  <span><Users size={10} className="inline mr-1" />{s.expected_attendance}</span>
                </div>
              </button>
            ))}
            {sessions.length === 0 && <div className="text-zinc-600 text-sm py-8 text-center">No sessions scheduled</div>}
          </div>
        </Panel>

        {/* WOD editor */}
        <Panel className="lg:col-span-8">
          {selected ? (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-volt font-semibold">Workout of the Day</div>
                  <h2 className="font-display text-3xl font-black uppercase tracking-tight mt-1">{selected.title}</h2>
                  <div className="text-xs uppercase tracking-[0.16em] text-zinc-500 mt-1">
                    {SESSION_TYPE_LABEL[selected.session_type] || selected.session_type} · {selected.duration_min} min · {new Date(selected.start_time).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                </div>
              </div>

              {/* AI generator */}
              <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                <div className="md:col-span-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Level</div>
                  <Select value={wodLevel} onValueChange={setWodLevel}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="wod-level"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Beginner">Beginner</SelectItem>
                      <SelectItem value="Intermediate">Intermediate</SelectItem>
                      <SelectItem value="Advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-6">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Focus / constraints</div>
                  <input value={wodFocus} onChange={(e) => setWodFocus(e.target.value)}
                    placeholder="e.g. shoulder-friendly, no barbell"
                    className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-sm h-10 px-3 text-sm" />
                </div>
                <div className="md:col-span-3">
                  <Button onClick={() => genWOD.mutate()} disabled={genWOD.isPending}
                    data-testid="ai-wod-button"
                    className="w-full bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
                    <Sparkles size={14} className="mr-1" /> {genWOD.isPending ? "Generating…" : "AI Generate"}
                  </Button>
                </div>
              </div>

              {/* WOD body */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Workout (markdown)</div>
                <Textarea
                  value={selected.wod || ""}
                  onChange={(e) => setSelected({ ...selected, wod: e.target.value })}
                  rows={14}
                  data-testid="wod-textarea"
                  placeholder="Warm-up — 5 min Row…&#10;**Strength** — 5x5 Back Squat @ 75%&#10;**Conditioning** — 12 min AMRAP…"
                  className="bg-zinc-950 border-zinc-900 font-mono-cmd text-sm leading-relaxed"
                />
                <div className="mt-2 flex justify-end">
                  <Button
                    onClick={() => updateSession.mutate({ id: selected.id, body: { wod: selected.wod } })}
                    data-testid="save-wod-button"
                    className="bg-zinc-900 border border-zinc-800 hover:border-volt text-volt uppercase tracking-wider rounded-sm">
                    Save WOD
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FieldBox label="Coaching Notes" value={selected.coaching_notes} onSave={(v) => updateSession.mutate({ id: selected.id, body: { coaching_notes: v } })} />
                <FieldBox label="Regressions" value={selected.regressions} onSave={(v) => updateSession.mutate({ id: selected.id, body: { regressions: v } })} />
                <FieldBox label="Progressions" value={selected.progressions} onSave={(v) => updateSession.mutate({ id: selected.id, body: { progressions: v } })} />
              </div>
            </div>
          ) : (
            <div className="text-zinc-600 text-sm py-12 text-center">Select a session to program its WOD.</div>
          )}
        </Panel>
      </div>

      {/* Tasks */}
      <Panel>
        <PanelHeader subtitle="Action Items" title="Follow-ups & Renewals" />
        {tasks.length === 0 ? (
          <div className="text-zinc-600 text-sm py-8 text-center">All clear — no open tasks.</div>
        ) : (
          <div className="space-y-2">
            {tasks.filter((t) => t.status === "OPEN").map((t) => (
              <div key={t.id} className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 flex items-center gap-3" data-testid={`task-${t.id}`}>
                <div className="flex-1">
                  <div className="font-semibold text-sm">{t.member_name || t.member_id.slice(0, 8)}</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{t.task_type.replace("_", " ")}</div>
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={async () => {
                    try {
                      await apiClient.post("/whatsapp/send", {
                        to_phone: t.member_phone || "+910000000000",
                        template: t.task_type === "RENEWAL" ? "gym_renewal_reminder" : "gym_followup",
                        params: [t.member_name || "Member"],
                      });
                      toast.success("Nudge queued (Gupshup)");
                    } catch (e) {
                      toast.error("WhatsApp send failed");
                    }
                  }}
                  data-testid={`nudge-${t.id}`}
                  className="bg-zinc-900 border-zinc-800 hover:border-volt text-volt">
                  <Send size={12} className="mr-1" /> Nudge
                </Button>
                <Button size="sm"
                  onClick={() => completeTask.mutate({ id: t.id, body: { status: "DONE", reason: "Completed" } })}
                  data-testid={`done-${t.id}`}
                  className="bg-volt hover:bg-volt-hover text-black uppercase font-bold">
                  Done
                </Button>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function FieldBox({ label, value, onSave }) {
  const [v, setV] = useState(value || "");
  React.useEffect(() => { setV(value || ""); }, [value]);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">{label}</div>
      <Textarea value={v} onChange={(e) => setV(e.target.value)} onBlur={() => onSave(v)} rows={4}
        className="bg-zinc-950 border-zinc-900 text-sm font-mono-cmd" placeholder={`Add ${label.toLowerCase()}…`} />
    </div>
  );
}
